import { notNull, parallelMap } from "@/utils/helpers";
import type { ErrorResult } from "@/utils/types";
import type { SearchParamsSync } from "@/utils/types";
import type { PostgrestError } from "@supabase/supabase-js";
import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          // Every size token in tailwind.config.ts, or a text color after
          // one of these in cn() replaces it instead of joining it.
          text: [
            "xs-12",
            "sm-13",
            "sm-14",
            "lg-18",
            "listen-10",
            "listen-11",
            "listen-12",
            "listen-13",
            "listen-14",
            "listen-20",
            "listen-28",
            "listen-32",
          ],
        },
      ],
      rounded: [
        {
          rounded: [
            "listen-2",
            "listen-4",
            "listen-8",
            "listen-12",
            "listen-16",
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}

export function paginate<T>(arr: T[], size: number) {
  return arr.reduce((acc, val, i) => {
    const idx = Math.floor(i / size);
    const page = acc[idx] || (acc[idx] = []);
    page.push(val);

    return acc;
  }, [] as T[][]);
}

export const uniqueSorted = <T extends string>(values: T[]): T[] =>
  Array.from(new Set(values)).toSorted((a, b) => a.localeCompare(b));

export const uniqueSortedNumbers = <T extends number>(values: T[]): T[] =>
  Array.from(new Set(values)).toSorted((a, b) => a - b);

export function promiseChain<T>(
  ...fns: Array<(state: T) => Promise<T> | T>
): (initial: T) => Promise<T> {
  return (initial) =>
    fns.reduce<Promise<T>>(
      async (accPromise, fn) => fn(await accPromise),
      Promise.resolve(initial)
    );
}

/**
 * Returns a function that runs an async function without awaiting but catches errors.
 * The function can have any number of arguments and must return Promise<void>.
 */
export const safeAsync = <Args extends unknown[]>(
  fn: (...args: Args) => Promise<unknown>
): ((...args: Args) => void) => {
  return (...args: Args) => {
    fn(...args).catch((error) => {
      console.error(error);
    });
  };
};

export const tryCatch = async <T>(
  promise: Promise<T>
): Promise<[null, T] | [Error]> => {
  try {
    return [null, await promise];
  } catch (e) {
    return [e instanceof Error ? e : new Error(String(e))];
  }
};

export const catchError = <T>(fn: () => T): ErrorResult<T> => {
  try {
    const result = fn();
    return { data: result, error: null };
  } catch (e) {
    if (e instanceof Error) {
      console.log("Caught error", e.message);
      return { data: null, error: { message: e.message } };
    } else {
      throw e;
    }
  }
};

export function aggregateSupabaseFetchResults<T>(
  results: (
    | { data: T[]; error: null }
    | { error: PostgrestError; data: null }
  )[]
) {
  // Just returning the first error we encounter
  const firstError = results.find((r) => r.error);
  if (firstError) return firstError;
  return { data: results.flatMap((r) => r.data).filter(notNull), error: null };
}

export async function collectPaginatedOrderedSupabaseQuery<T>(
  query: (
    offset: number,
    limit: number
  ) => PromiseLike<
    { data: T[]; error: null } | { error: PostgrestError; data: null }
  >,
  pageSize: number = 100
) {
  const results = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const { data, error } = await query(offset, pageSize);
    if (error) {
      return { data: null, error };
    }
    results.push(...data);
    offset += pageSize;
    hasMore = data.length === pageSize;
  }
  return { data: results, error: null };
}

export async function batchedSupabaseQuery<T, U>(
  arr: T[],
  query: (
    ids: T[]
  ) => PromiseLike<
    { data: U[]; error: null } | { error: PostgrestError; data: null }
  >,
  size: number = 40
) {
  // Input array is broken up into chunks of `size`, and at most 50 chunks are run in parallel.
  const results = await parallelMap(
    paginate(arr, size),
    async (data) => await query(data),
    50
  );
  return aggregateSupabaseFetchResults(results);
}

export async function batchedSupabaseUpdate<T>(
  arr: T[],
  query: (
    ids: T[]
  ) => PromiseLike<
    { data: null; error: null } | { error: PostgrestError; data: null }
  >,
  size: number = 40,
  concurrency: number = 20
) {
  const results = await parallelMap(
    paginate(arr, size),
    async (ids) => await query(ids),
    concurrency
  );
  return results.flat().find((r) => r.error) ?? { data: null, error: null };
}

export function groupBy<T, K extends PropertyKey>(
  arr: T[],
  key: (item: T) => K
) {
  return arr.reduce(
    (acc, item) => {
      const group = key(item);
      acc[group] = acc[group] || [];
      acc[group].push(item);
      return acc;
    },
    {} as Record<K, T[]>
  );
}

export type SerializedBitset = string & { __brand: "SerializedBitset" };

// Bitset encoding for readable_id exclusion: bit N = "client has readable_id N".
// ~1.6KB for 13,000 responses, zero false positives, O(1) lookup.
export const createIdBitset = (readableIds: number[]): SerializedBitset => {
  if (readableIds.length === 0) return "" as SerializedBitset;
  let max = 0;
  for (const id of readableIds) {
    if (id > max) max = id;
  }
  const bytes = new Uint8Array(Math.ceil((max + 1) / 8));
  for (const id of readableIds) {
    bytes[id >>> 3] |= 1 << (id & 7);
  }
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary) as SerializedBitset;
};

export const deserializeIdBitset = (
  base64: SerializedBitset
): ((readableId: number) => boolean) => {
  let raw: string;
  try {
    raw = atob(base64);
  } catch {
    console.warn("Malformed exclude_id_bitset, ignoring");
    return () => false;
  }
  return (readableId: number) => {
    const byteIndex = readableId >>> 3;
    if (byteIndex >= raw.length) return false;
    return (raw.charCodeAt(byteIndex) & (1 << (readableId & 7))) !== 0;
  };
};

export function isUUID(text: string) {
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(text);
}

export async function sleep(time_ms: number) {
  await new Promise<void>((resolve) => setTimeout(resolve, time_ms));
}

/**
 * Exponential backoff with additive jitter.
 * Returns a delay in [baseDelayMs, 2 * baseDelayMs) where
 * baseDelayMs = min(initialDelayMs * backoffMultiplier^attempt, maxBaseDelayMs).
 */
export const exponentialBackoffWithJitterMs = ({
  attempt,
  initialDelayMs,
  maxBaseDelayMs,
  backoffMultiplier,
}: {
  attempt: number;
  initialDelayMs: number;
  maxBaseDelayMs: number;
  backoffMultiplier: number;
}): number => {
  const baseDelayMs = Math.min(
    initialDelayMs * Math.pow(backoffMultiplier, attempt),
    maxBaseDelayMs
  );
  const jitterMs = baseDelayMs * Math.random();
  return baseDelayMs + jitterMs;
};

/**
 * Retry a function with backoff delays between attempts.
 * @returns The result of the function, or throws if all attempts fail.
 */
export const retryWithBackoff = async <T>({
  fn,
  backoffs,
  shouldAbort,
  shouldRetry,
  onRetry,
}: {
  fn: (attempt: number) => Promise<T>;
  backoffs: number[];
  shouldAbort?: () => boolean;
  shouldRetry?: (error: unknown) => boolean;
  onRetry?: (attempt: number, delay: number) => void;
}): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= backoffs.length; attempt++) {
    if (shouldAbort?.()) {
      throw new Error("Aborted");
    }
    try {
      return await fn(attempt);
    } catch (error) {
      lastError = error;
      if (attempt < backoffs.length && (shouldRetry?.(error) ?? true)) {
        const delay = backoffs[attempt];
        onRetry?.(attempt, delay);
        await sleep(delay);
      } else {
        throw error;
      }
    }
  }
  throw lastError;
};

export function maxBy<T, K extends string | number>(
  arr: T[],
  key: (item: T) => K
) {
  if (arr.length === 0) return undefined;
  return arr.reduce((max, item) => (key(item) > key(max) ? item : max), arr[0]);
}

export function sumBy<T>(arr: readonly T[], value: (item: T) => number) {
  return arr.reduce((total, item) => total + value(item), 0);
}

// Portable replacement for Set.prototype.intersection (ES2025). Landed in
// V8 12.4 / Node 22 (https://nodejs.org/en/blog/release/v22.0.0); we pin
// Node 20 (`engines` in package.json, also the Vercel server runtime), so
// calling the native method during SSR throws "intersection is not a function".
// Mirrors the "iterate the smaller, look up in the larger" shape of the V8
// builtin: https://chromium.googlesource.com/v8/v8/+/refs/heads/main/src/builtins/set-intersection.tq
export function setIntersection<T>(a: Set<T>, b: Iterable<T>): Set<T> {
  const result = new Set<T>();
  const [smaller, larger] =
    a.size <= (b instanceof Set ? b.size : Infinity)
      ? [a, b]
      : [b as Set<T>, a];
  const largerSet = larger instanceof Set ? larger : new Set(larger);
  for (const value of smaller) {
    if (largerSet.has(value)) result.add(value);
  }
  return result;
}

export const cleanFileName = (name: string, extension: string) =>
  `${name.replace(/[^a-zA-Z\s_-]/g, "").slice(0, 50)}.${extension}`;

/**
 * Joins an array of strings with proper grammar, using "and" for the last item.
 * Examples:
 * - ["apple"] -> "apple"
 * - ["apple", "banana"] -> "apple and banana"
 * - ["apple", "banana", "cherry"] -> "apple, banana, and cherry"
 */
export function joinWithAnd(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  const itemsCopy = [...items];
  const last = itemsCopy.pop()!;
  return `${itemsCopy.join(", ")}, and ${last}`;
}

/**
 * @param error The error, usually caught from a try-catch block. Has type unknown but expected to usually be an Error.
 */
export const errorToMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const truncate = (text: string, maxChars: number) => {
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars - 3) + "...";
};

export type Jsonable =
  | string
  | number
  | boolean
  | null
  | Jsonable[]
  | { [key: string]: Jsonable };

export type JsonableObject = { [key: string]: Jsonable };

export function jsonStringify(j: Jsonable): string {
  return JSON.stringify(j);
}

// Carries the current query across a redirect. Without it a migrated link
// drops ?t= and ?message=, and the new URL can no longer open what it named.
export const buildSearchSuffix = (
  searchParams: SearchParamsSync,
  omitKeys: readonly string[] = []
): string => {
  const query = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (omitKeys.includes(key)) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => query.append(key, entry));
    } else if (notNull(value)) {
      query.set(key, value);
    }
  });
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
};
